"use client";
import React from "react";
import { Box, Container, Grid, Typography, Card, CardContent, Chip } from "@mui/material";
import { SchoolRounded, VolunteerActivismRounded, InsightsRounded, BoltRounded } from "@mui/icons-material";
import Header from "./header";
import Footer from "./footer";
import { headerLikeBg, heroSx, titleGradient } from "./components/ui/themeBits";

export const metadata = {
  title: "Our Mission | AISECT Inter College",
  description:
    "Our mission is to nurture curious, confident, and compassionate learners with strong values and future-ready skills.",
  openGraph: { title: "Our Mission | AISECT Inter College" },
};

export default function MissionPage() {
  return (
    <>
      <Header />

      {/* HERO */}
      <Box sx={heroSx}>
        <Container maxWidth="lg">
          <Typography component="h1" align="center" sx={titleGradient}>
            Our Mission
          </Typography>
          <Typography align="center" sx={{ mt: 1, color: "text.secondary", fontSize: { xs: 15, sm: 16 } }}>
            What we do every day to serve students, families, and the community.
          </Typography>
        </Container>
      </Box>

      {/* MAIN */}
      <Container maxWidth="lg" sx={{ my: { xs: 3.5, sm: 5 } }}>
        <Grid container spacing={{ xs: 2.5, sm: 3 }}>
          {/* Mission statement */}
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ p: { xs: 2.25, sm: 2.75 }, borderRadius: 3, ...headerLikeBg }}>
              <Typography sx={{ fontWeight: 800, fontSize: "clamp(20px, 2.6vw, 28px)", mb: 1 }}>
                What drives us
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "clamp(16.5px, 1.9vw, 19px)", lineHeight: 1.9 }}>
                Our mission is to provide an inclusive and inspiring learning environment that cultivates
                curiosity, character, and competence. We focus on strong fundamentals, digital literacy, and
                values-based education so that every learner becomes a responsible citizen and a life-long learner.
              </Typography>
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip label="Excellence" />
                <Chip label="Inclusivity" />
                <Chip label="Integrity" />
                <Chip label="Service" />
              </Box>
            </Card>
          </Grid>

          {/* Pillars (cards) */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              {[
                { icon: <SchoolRounded />, title: "Strong Academics", text: "Mastery of core subjects with conceptual clarity and regular assessment." },
                { icon: <BoltRounded />, title: "Future Skills", text: "Digital literacy, communication, collaboration, and problem-solving." },
                { icon: <InsightsRounded />, title: "Holistic Growth", text: "Sports, arts, leadership, and life-skills for balanced development." },
                { icon: <VolunteerActivismRounded />, title: "Community & Values", text: "Service, empathy, and responsibility towards society and environment." },
              ].map((p, i) => (
                <Grid item xs={12} key={i}>
                  <Card elevation={0} sx={{ p: 2, borderRadius: 3, ...headerLikeBg }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                      <Box sx={{ mt: "2px" }}>{p.icon}</Box>
                      <Box>
                        <Typography
  sx={{
    fontWeight: 800,
    fontSize: "clamp(14px, 1.8vw, 16px)", // larger title
  }}
>
  {p.title}
</Typography>
<Typography
  sx={{
    color: "text.secondary",
    fontSize: "clamp(12px, 1.4vw, 14px)", // larger body text
    lineHeight: 1.7,
  }}
>
  {p.text}
</Typography>

                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Callout */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...headerLikeBg }}>
              <Typography sx={{ fontWeight: 800, fontSize: "clamp(18px, 2.2vw, 22px)", mb: 0.5 }}>
                Every student matters
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                We strive to ensure that no learner is left behind—through mentoring, bridge courses, and
                personalized support paths.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </>
  );
}
